import { forwardRef, useId } from 'react';
import { cn } from '../lib/cn';

export interface FieldProps {
  label?: string;
  hint?: string;
  /** Mensagem de erro. Presente, substitui o hint e marca o controle invalido. */
  error?: string;
}

/** Casca compartilhada por Input, Textarea e Select: rotulo, dica e erro. */
function Field({
  id,
  label,
  hint,
  error,
  children,
  className,
}: FieldProps & { id: string; children: React.ReactNode; className?: string }) {
  const message = error ?? hint;
  return (
    <div className={cn('flex w-full flex-col gap-1', className)}>
      {label !== undefined && (
        <label htmlFor={id} className="text-[12px] font-semibold text-secondary">
          {label}
        </label>
      )}
      {children}
      {message !== undefined && (
        <p
          id={`${id}-msg`}
          className={cn('text-[11px]', error !== undefined ? 'text-error' : 'text-tertiary')}
        >
          {message}
        </p>
      )}
    </div>
  );
}

export const controlClasses =
  'w-full rounded-wc-md border bg-raised px-3 py-[9px] text-[14px] text-primary placeholder:text-tertiary transition-[border-color,box-shadow] duration-[var(--duration-micro)] outline-none focus:border-wc-blue focus:shadow-[0_0_0_3px_rgba(29,102,255,0.12)] disabled:cursor-not-allowed disabled:opacity-60';

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>, FieldProps {
  /** Icone a esquerda, 16px. Ja recebe cor e posicao. */
  icon?: React.ReactNode;
  /** Acao a direita do campo: mostrar senha, limpar, escolher. */
  rightIcon?: React.ReactNode;
  wrapperClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, wrapperClassName, label, hint, error, icon, rightIcon, id, ...props },
  ref,
) {
  const generated = useId();
  const fieldId = id ?? generated;

  return (
    <Field
      id={fieldId}
      {...(label !== undefined ? { label } : {})}
      {...(hint !== undefined ? { hint } : {})}
      {...(error !== undefined ? { error } : {})}
      {...(wrapperClassName !== undefined ? { className: wrapperClassName } : {})}
    >
      <div className="relative">
        {icon !== undefined && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-[10px] -translate-y-1/2 text-tertiary"
          >
            {icon}
          </span>
        )}
        <input
          ref={ref}
          id={fieldId}
          aria-invalid={error !== undefined || undefined}
          aria-describedby={
            error !== undefined || hint !== undefined ? `${fieldId}-msg` : undefined
          }
          className={cn(
            controlClasses,
            error !== undefined ? 'border-error' : 'border-strong',
            icon !== undefined && 'pl-9',
            rightIcon !== undefined && 'pr-10',
            className,
          )}
          {...props}
        />
        {rightIcon !== undefined && (
          <span className="absolute top-1/2 right-2.5 -translate-y-1/2 text-tertiary">
            {rightIcon}
          </span>
        )}
      </div>
    </Field>
  );
});

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>, FieldProps {
  wrapperClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, wrapperClassName, label, hint, error, id, rows = 4, ...props },
  ref,
) {
  const generated = useId();
  const fieldId = id ?? generated;

  return (
    <Field
      id={fieldId}
      {...(label !== undefined ? { label } : {})}
      {...(hint !== undefined ? { hint } : {})}
      {...(error !== undefined ? { error } : {})}
      {...(wrapperClassName !== undefined ? { className: wrapperClassName } : {})}
    >
      <textarea
        ref={ref}
        id={fieldId}
        rows={rows}
        aria-invalid={error !== undefined || undefined}
        aria-describedby={error !== undefined || hint !== undefined ? `${fieldId}-msg` : undefined}
        className={cn(
          controlClasses,
          'resize-y leading-[1.6]',
          error !== undefined ? 'border-error' : 'border-strong',
          className,
        )}
        {...props}
      />
    </Field>
  );
});

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'>, FieldProps {
  wrapperClassName?: string;
}

/**
 * Select nativo de proposito. O do sistema ja traz teclado, leitor de tela e o
 * seletor em roda no mobile - nenhum menu customizado empata com isso.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, wrapperClassName, label, hint, error, id, children, ...props },
  ref,
) {
  const generated = useId();
  const fieldId = id ?? generated;

  return (
    <Field
      id={fieldId}
      {...(label !== undefined ? { label } : {})}
      {...(hint !== undefined ? { hint } : {})}
      {...(error !== undefined ? { error } : {})}
      {...(wrapperClassName !== undefined ? { className: wrapperClassName } : {})}
    >
      <div className="relative">
        <select
          ref={ref}
          id={fieldId}
          aria-invalid={error !== undefined || undefined}
          aria-describedby={
            error !== undefined || hint !== undefined ? `${fieldId}-msg` : undefined
          }
          className={cn(
            controlClasses,
            'cursor-pointer appearance-none pr-9',
            error !== undefined ? 'border-error' : 'border-strong',
            className,
          )}
          {...props}
        >
          {children}
        </select>
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-tertiary"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </div>
    </Field>
  );
});
